import { useEffect, useRef } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dropdown } from '@/components/ui/Dropdown';
import { SkeletonAvatarRow } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { notificationsApi } from '@/features/notifications/api';
import { formatRelativeTime } from '@/utils/format';
import { useBrowserNotifications } from '@/hooks/useBrowserNotifications';
import { useAuth } from '@/hooks/useAuth';

export function NotificationsPanel() {
  const queryClient = useQueryClient();
  const browserNotifications = useBrowserNotifications();
  const { roles } = useAuth();
  const notificationsHome = roles.includes('client') ? '/portal/notifications' : '/notifications';
  const lastSeenIdRef = useRef<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'panel'],
    queryFn: () => notificationsApi.list(1, 8),
    refetchInterval: 60_000,
  });

  const unreadCount = data?.meta.unread_count ?? 0;
  const newest = data?.data[0];

  // Fire a background-tab browser notification when a genuinely new
  // notification shows up between polls — not on first load.
  useEffect(() => {
    if (!newest) return;
    if (lastSeenIdRef.current !== null && newest.id !== lastSeenIdRef.current && !newest.is_read) {
      browserNotifications.notify(newest.title, newest.body ?? undefined);
    }
    lastSeenIdRef.current = newest.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newest?.id]);

  async function handleMarkAllRead() {
    await notificationsApi.markAllRead();
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }

  async function handleOpen(id: number, isRead: boolean) {
    if (!isRead) {
      await notificationsApi.markRead(id);
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  }

  return (
    <Dropdown
      width={360}
      trigger={({ onClick, isOpen }) => (
        <button
          type="button"
          onClick={onClick}
          aria-label="Notifications"
          className={`relative flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100 ${
            isOpen ? 'bg-gray-100 dark:bg-gray-800' : ''
          }`}
        >
          <Bell className="h-[18px] w-[18px]" />
          {unreadCount > 0 && (
            <span className="absolute right-1.5 top-1.5 flex h-2 w-2 items-center justify-center rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-900" />
          )}
        </button>
      )}
    >
      {() => (
        <div>
          <div className="flex items-center justify-between px-2.5 py-1.5">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</p>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void handleMarkAllRead()}
                className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto px-1 py-1">
            {isLoading && <SkeletonAvatarRow rows={3} />}
            {!isLoading && (data?.data.length ?? 0) === 0 && (
              <EmptyState icon={Bell} title="You're all caught up" description="No notifications yet." />
            )}
            {data?.data.map((notification) => (
              <button
                key={notification.id}
                type="button"
                onClick={() => void handleOpen(notification.id, notification.is_read)}
                className={`flex w-full flex-col gap-0.5 rounded-xl px-2.5 py-2.5 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${
                  notification.is_read ? '' : 'bg-brand-50/60 dark:bg-brand-950/30'
                }`}
              >
                <div className="flex items-start gap-2">
                  {!notification.is_read && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />}
                  <p className={`text-sm ${notification.is_read ? 'text-gray-500 dark:text-gray-400' : 'font-medium text-gray-900 dark:text-gray-100'}`}>
                    {notification.title}
                  </p>
                </div>
                <p className="pl-3.5 text-xs text-gray-400">{formatRelativeTime(notification.created_at)}</p>
              </button>
            ))}
          </div>
          <Link
            to={notificationsHome}
            className="block border-t border-gray-100 px-2.5 py-2 text-center text-xs font-medium text-brand-600 hover:underline dark:border-gray-800 dark:text-brand-400"
          >
            View all
          </Link>
        </div>
      )}
    </Dropdown>
  );
}
